import {
  fetchConvertedSessions,
  FetchFuncParams,
  fetchSessions,
  fetchVisitors,
  fetchVisits,
} from '@va/dashboard/api-client/graphs';
import { useCurrentPeriodFilter, usePreviousPeriodFilter } from '@va/dashboard/util-hooks';
import { TimePeriod } from '@va/types/time';
import { BaseTimeseriesResponse } from '@va/types/time-series';
import { CHART_COLORS_ARRAY } from '@va/ui/design-system';
import { chartHelpers } from '@va/util/helpers';
import { useLazyDataFetch } from '@va/util/hooks';
import { useCallback, useMemo } from 'react';

export const DATASET_KEYS_MAP = {
  sessions: 'sessions',
  visits: 'visits',
  visitors: 'visitors',
  newVisitors: 'newVisitors',
  convertedSessions: 'convertedSessions',
} as const;

export const DATASET_KEYS_LIST = Object.values(DATASET_KEYS_MAP);

export const API_LABEL_MAP = {
  overallSessions: 'Overall Sessions',
  overallVisits: 'Overall Visits',
  overallVisitors: 'Overall Visitors',
  newVisitors: 'New Visitors',
  convertedSessions: 'Converted Sessions',
};

export const API_LABEL_TO_KEY_MAP = {
  [API_LABEL_MAP.overallSessions]: DATASET_KEYS_MAP.sessions,
  [API_LABEL_MAP.overallVisits]: DATASET_KEYS_MAP.visits,
  [API_LABEL_MAP.overallVisitors]: DATASET_KEYS_MAP.visitors,
  [API_LABEL_MAP.newVisitors]: DATASET_KEYS_MAP.newVisitors,
  [API_LABEL_MAP.convertedSessions]: DATASET_KEYS_MAP.convertedSessions,
};

export const COLORS_MAP = {
  [DATASET_KEYS_MAP.visits]: CHART_COLORS_ARRAY[0],
  [DATASET_KEYS_MAP.sessions]: CHART_COLORS_ARRAY[1],
  [DATASET_KEYS_MAP.newVisitors]: CHART_COLORS_ARRAY[3],
  [DATASET_KEYS_MAP.visitors]: CHART_COLORS_ARRAY[4],
  [DATASET_KEYS_MAP.convertedSessions]: CHART_COLORS_ARRAY[2],
};

export type WebsiteActivityData = BaseTimeseriesResponse<(typeof DATASET_KEYS_MAP)[keyof typeof DATASET_KEYS_MAP]>;

const useWebsiteActivityData = (
  websiteId: string,
  { from, unit, until }: { from: string; unit: TimePeriod; until: string },
) => {
  const query = useMemo(
    () => ({
      from,
      until,
      type: 'time-series',
      unit,
    }),
    [from, unit, until],
  );

  const fetcher = useCallback(
    async (params: FetchFuncParams): Promise<WebsiteActivityData> => {
      const responses = await fetchWebsiteActivityData(params);
      const filteredResponses = responses
        .map((res) => ({
          ...res.payload,
          datasets: res.payload.datasets.filter(({ label }) => !!API_LABEL_TO_KEY_MAP[label]),
        }))
        .filter((res) => res.datasets.length);

      return chartHelpers.buildBaseTimeseriesResponse(filteredResponses, {
        extractDatasetKey: (dataset) => API_LABEL_TO_KEY_MAP[dataset.label],
        getDatasetColor: (dataset) => {
          return COLORS_MAP[API_LABEL_TO_KEY_MAP[dataset.label]] ?? window.PRIMARY_COLOR;
        },
        getDatasetLabel: (dataset) => {
          const key = API_LABEL_TO_KEY_MAP[dataset.label];
          return `all.manageWebsites.kpi.${key}`;
        },
        until: query['until'],
        unit: query['unit'] ?? 'day',
      });
    },
    [query],
  );

  return useLazyDataFetch<WebsiteActivityData>(
    [{ websiteId, body: {}, query } as FetchFuncParams, 'visits-sessions-visitors'],
    undefined,
    undefined,
    fetcher,
  );
};

export const useCurrentWebsiteActivityData = (websiteId: string) => {
  const currentPeriodFilter = useCurrentPeriodFilter();

  return useWebsiteActivityData(websiteId, currentPeriodFilter);
};

export const usePreviousWebsiteActivityData = (websiteId: string) => {
  const previousPeriodFilter = usePreviousPeriodFilter();

  return useWebsiteActivityData(websiteId, previousPeriodFilter);
};

const fetchWebsiteActivityData = (params: FetchFuncParams) => {
  return Promise.all([
    fetchVisits(params),
    fetchSessions(params),
    fetchVisitors(params),
    fetchConvertedSessions(params),
  ]);
};
